import { Box, Typography, makeStyles, Theme, Tooltip } from '@material-ui/core';
import { Error } from '@material-ui/icons';

export interface EntityNameComparisonTableProps {
  data: Record<string, any>;
}

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    fontWeight: 700,
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
  },
  table: {
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    overflow: 'hidden',
  },
  head: {
    display: 'flex',
    backgroundColor: '#FAFAFA',
    borderBottom: '1px solid #E0E0E0',
  },
  row: {
    display: 'flex',
  },
  cell: {
    flex: 1,
    padding: theme.spacing(1.5, 2),
    minWidth: 0,
  },
  cellContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  flagIcon: {
    fontSize: '1.125rem',
    flexShrink: 0,
  },
  divider: {
    borderRight: '1px solid #E0E0E0',
  },
  columnTitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontWeight: 'bold',
    letterSpacing: '0.05em',
  },
  value: {
    fontSize: '0.875rem',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
  },
  empty: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
}));

const ENTITY_NAME = 'borrowerEntityName';

export const EntityNameComparisonTable = ({ data }: EntityNameComparisonTableProps) => {
  const classes = useStyles();
  const entityName = (data?.items ?? []).find(item => item.itemName === ENTITY_NAME);
  if (!entityName) {
    return null;
  }
  const cells = [entityName.expectedData, entityName.data];
  return (
    <>
      <Typography className={classes.heading}>Entity Name</Typography>
      <Box className={classes.table}>
        <Box className={classes.head}>
          {['PORTAL', 'DOCUMENT'].map((title, index) => (
            <Box className={`${classes.cell} ${index === 0 ? classes.divider : ''}`} key={title}>
              <Typography className={classes.columnTitle}>{title}</Typography>
            </Box>
          ))}
        </Box>
        <Box className={classes.row}>
          {cells.map((value, index) => (
            <Box
              className={`${classes.cell} ${index === 0 ? classes.divider : ''}`}
              key={index === 0 ? 'portal' : 'document'}
            >
              <Box className={classes.cellContent}>
                {index === 0 && entityName.needsRevisionFlag && (
                  <Tooltip title="The extracted entity name does not match the portal value.">
                    <Error className={classes.flagIcon} color="error" />
                  </Tooltip>
                )}
                <Typography className={value ? classes.value : classes.empty}>
                  {value || '—'}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

export default EntityNameComparisonTable;
